import { memo } from 'react';
import { TrendingDown, Package } from 'lucide-react';

interface AlertPillsProps {
  readonly priceDropCount: number;
  readonly backInStockCount: number;
  readonly activeFilter?: string;
  readonly onFilter: (filter: string) => void;
}

export const AlertPills = memo(function AlertPills({ 
  priceDropCount, 
  backInStockCount,
  activeFilter,
  onFilter,
}: AlertPillsProps) {
  if (priceDropCount === 0 && backInStockCount === 0) return null;

  return (
    <div className="grid grid-cols-2 gap-3 mb-5">
      {priceDropCount > 0 && (
        <AlertPill
          filterKey="price-drops"
          icon={TrendingDown}
          label={`${priceDropCount} on sale`}
          colorClass="priceDrop"
          isActive={activeFilter === 'price-drops'}
          onFilter={onFilter}
        />
      )}
      {backInStockCount > 0 && (
        <AlertPill
          filterKey="back-in-stock"
          icon={Package}
          label={`${backInStockCount} in stock`}
          colorClass="stockAlert"
          isActive={activeFilter === 'back-in-stock'}
          onFilter={onFilter}
        />
      )}
    </div>
  );
});

interface AlertPillProps {
  readonly filterKey: string;
  readonly icon: typeof TrendingDown;
  readonly label: string;
  readonly colorClass: 'priceDrop' | 'stockAlert';
  readonly isActive: boolean;
  readonly onFilter: (filter: string) => void;
}

const AlertPill = memo(function AlertPill({ 
  filterKey,
  icon: Icon, 
  label, 
  colorClass,
  isActive,
  onFilter,
}: AlertPillProps) {
  return (
    <button
      onClick={() => onFilter(isActive ? 'all' : filterKey)}
      className={`flex items-center justify-center gap-2 px-4 py-3 rounded-2xl transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] ${
        isActive
          ? `bg-${colorClass}/25 border-2 border-${colorClass}/50`
          : `bg-${colorClass}/10 border border-${colorClass}/20 hover:bg-${colorClass}/15`
      }`}
    >
      <Icon className={`w-4 h-4 text-${colorClass}`} strokeWidth={2.5} />
      <span className={`text-sm font-medium text-${colorClass}`}>
        {label}
      </span>
    </button>
  );
});
